import React, { useState } from 'react';
import toast from 'react-hot-toast';
import Modal from '../Modal';
import CohortPicker from './CohortPicker';
import {
  jobsError,
  useCampaigns,
  useCreateCampaign,
  usePreviewCampaign,
} from '../../hooks/useJobDigest';
import {
  Badge,
  Card,
  count,
  date,
  Empty,
  INPUT,
  PRIMARY,
  SECONDARY,
  Stat,
} from './ui';

function PreviewResult({ preview }) {
  const p = preview.preview;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Stat label="Learners in scope" value={count(p.graduates)} />
        <Stat
          label="Would get an email"
          value={count(p.digests)}
          hint="At least 3 matching jobs"
        />
        <Stat
          label="Skipped, too few jobs"
          value={count(p.skipped_too_few_jobs)}
        />
        <Stat
          label="Jobs to choose from"
          value={count(p.candidate_jobs)}
          hint="Fresh, junior, open to Kenya"
        />
      </div>
      {p.left_for_next_run > 0 && (
        <p className="rounded-lg bg-amber-50 px-4 py-3 text-xs text-amber-800">
          The server sends at most {count(preview.max_emails_per_run)} emails
          per run, so {count(p.left_for_next_run)} graduates would wait for a
          later run.
        </p>
      )}
      {p.top_jobs?.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">
            Jobs that would be sent most
          </h3>
          <ul className="divide-y divide-gray-100 text-sm">
            {p.top_jobs.map((job) => (
              <li
                key={job.job_id}
                className="flex items-center justify-between py-1.5"
              >
                <span>
                  {job.title}
                  {job.company && (
                    <span className="text-gray-500"> · {job.company}</span>
                  )}
                </span>
                <span className="text-xs text-gray-500">
                  {count(job.count)} graduates
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function summaryLine(campaign) {
  const s = campaign.summary;
  if (!s) return '—';
  const parts = [];
  if (s.queued) parts.push(`${count(s.queued)} sent`);
  if (s.dry_run) parts.push(`${count(s.dry_run)} dry run`);
  if (s.failed) parts.push(`${count(s.failed)} failed`);
  if (s.skipped_too_few_jobs)
    parts.push(`${count(s.skipped_too_few_jobs)} too few jobs`);
  if (s.left_for_next_run)
    parts.push(`${count(s.left_for_next_run)} over the limit`);
  return parts.join(' · ') || 'No graduates matched';
}

export default function CampaignsTab({ cohortIds, setCohortIds, onView }) {
  const [name, setName] = useState('');
  const [confirm, setConfirm] = useState(null); // 'send' | 'dry'
  // Also send to learners who haven't been certified yet.
  const [includeMembers, setIncludeMembers] = useState(false);
  const preview = usePreviewCampaign();
  const create = useCreateCampaign();
  const { data: campaigns = [], isLoading, error } = useCampaigns();

  const previewData =
    preview.data &&
    preview.variables?.cohort_ids?.join() === cohortIds.join() &&
    preview.variables?.include_members === includeMembers
      ? preview.data
      : null;
  const wouldSend = previewData?.preview?.digests ?? 0;

  const runPreview = () =>
    preview.mutate(
      { cohort_ids: cohortIds, include_members: includeMembers },
      {
        onError: (err) => toast.error(jobsError(err, "Couldn't preview.")),
      }
    );

  const start = (dryRun) =>
    create.mutate(
      {
        name,
        cohort_ids: cohortIds,
        dry_run: dryRun,
        include_members: includeMembers,
      },
      {
        onSuccess: () => {
          toast.success(
            dryRun
              ? 'Dry run started. Nothing will be emailed.'
              : 'Campaign started. Emails are going out.'
          );
          setConfirm(null);
          setName('');
        },
        onError: (err) => {
          toast.error(jobsError(err, "Couldn't start the campaign."));
          setConfirm(null);
        },
      }
    );

  return (
    <div className="space-y-5">
      <Card title="New campaign">
        <div className="space-y-4">
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-700">Cohorts</span>
            <CohortPicker value={cohortIds} onChange={setCohortIds} />
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="mt-1"
              checked={includeMembers}
              onChange={(event) => setIncludeMembers(event.target.checked)}
            />
            <span>
              Include learners who aren&apos;t certified yet
              <span className="block text-xs text-gray-500">
                Sends to every active member of these cohorts, for programmes
                still in training. Certified members are always included.
              </span>
            </span>
          </label>
          <label className="block space-y-1">
            <span className="text-xs font-medium text-gray-700">
              Name (optional)
            </span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. HPF graduates, November"
              maxLength={200}
              className={`${INPUT} w-full`}
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className={SECONDARY}
              disabled={!cohortIds.length || preview.isLoading}
              onClick={runPreview}
            >
              {preview.isLoading ? 'Checking…' : 'Preview'}
            </button>
            <button
              type="button"
              className={SECONDARY}
              disabled={!previewData || create.isLoading}
              onClick={() => setConfirm('dry')}
              title="Saves who would get which jobs, without emailing anyone"
            >
              Dry run
            </button>
            <button
              type="button"
              className={PRIMARY}
              disabled={
                !previewData ||
                !previewData.sending_enabled ||
                !wouldSend ||
                create.isLoading
              }
              onClick={() => setConfirm('send')}
            >
              Send emails
            </button>
            {previewData && !previewData.sending_enabled && (
              <span className="text-xs text-gray-500">
                Sending is turned off on the server, so only dry runs are
                possible.
              </span>
            )}
          </div>
          {!previewData && cohortIds.length > 0 && (
            <p className="text-xs text-gray-500">
              Preview first to see who would be emailed. Nothing is saved or
              sent.
            </p>
          )}
          {previewData && <PreviewResult preview={previewData} />}
        </div>
      </Card>

      <Card title="Campaigns">
        {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
        {error && (
          <p className="text-sm text-red-700">
            {jobsError(error, "Couldn't load campaigns.")}
          </p>
        )}
        {!isLoading && !error && campaigns.length === 0 && (
          <Empty>No campaigns yet.</Empty>
        )}
        {campaigns.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b">
                  <th className="py-2 pr-3">Campaign</th>
                  <th className="py-2 pr-3">Cohorts</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2 pr-3">Started</th>
                  <th className="py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      <div className="font-medium text-gray-900">{c.name}</div>
                      <div className="text-xs text-gray-500">
                        {c.dry_run ? 'Dry run' : 'Emails'}
                        {c.include_members ? ' · incl. uncertified' : ''} ·{' '}
                        {c.created_by || '—'}
                      </div>
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-700">
                      {c.cohorts.map((co) => co.name || co.id).join(', ')}
                    </td>
                    <td className="py-2 pr-3">
                      <Badge status={c.status} />
                      {c.error && (
                        <div className="mt-1 max-w-xs text-xs text-red-700">
                          {c.error}
                        </div>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-700">
                      {summaryLine(c)}
                    </td>
                    <td className="py-2 pr-3 text-xs text-gray-500">
                      {date(c.created_at, true)}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-claret-500 hover:underline"
                        onClick={() => onView(c)}
                      >
                        View recommendations
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        modalOpen={Boolean(confirm)}
        title={confirm === 'send' ? 'Send job emails?' : 'Start a dry run?'}
        small
        onClose={() => !create.isLoading && setConfirm(null)}
      >
        <div className="space-y-4 text-sm text-gray-700">
          {confirm === 'send' ? (
            <p>
              About <b>{count(wouldSend)}</b>{' '}
              {includeMembers ? 'learners' : 'graduates'} in {cohortIds.length}{' '}
              cohort{cohortIds.length === 1 ? '' : 's'} will each get an email
              with up to 6 jobs. This can&apos;t be undone.
            </p>
          ) : (
            <p>
              This saves who would get which jobs, so you can review them under
              Recommendations. Nobody is emailed.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className={SECONDARY}
              disabled={create.isLoading}
              onClick={() => setConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={PRIMARY}
              disabled={create.isLoading}
              onClick={() => start(confirm !== 'send')}
            >
              {confirm === 'send'
                ? `Send ${count(wouldSend)} emails`
                : 'Start dry run'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
